import React, { useEffect, useMemo, useState } from "react";
import { QrCode, CheckCircle, XCircle, Info, SignOut, Camera, Keyboard, ShieldCheck } from "@phosphor-icons/react";
import { Logo, Stat, Spinner } from "../components/shared";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import QrScanner from "../components/QrScanner";
import { toast } from "sonner";
import axios from "axios";
import { API, apiErr } from "../lib/api";

const CK_KEY = "eventra_checker";

export default function CheckerPortal() {
  const [token, setToken] = useState(() => sessionStorage.getItem(CK_KEY) || "");
  const [tab, setTab] = useState("staff");
  const [form, setForm] = useState({ email: "", pin: "", event_code: "" });
  const [loading, setLoading] = useState(false);
  const [ctx, setCtx] = useState(null);
  const [eventId, setEventId] = useState("");
  const [checkpointId, setCheckpointId] = useState("");
  const [useCamera, setUseCamera] = useState(true);
  const [code, setCode] = useState("");
  const [result, setResult] = useState(null);
  const [stats, setStats] = useState(null);
  const [scanning, setScanning] = useState(false);

  const capi = useMemo(() => axios.create({ baseURL: API, headers: token ? { Authorization: `Bearer ${token}` } : {} }), [token]);

  const loadCtx = async () => {
    try {
      const { data } = await capi.get("/checkin/context");
      setCtx(data);
      if (data.events?.[0]) {
        setEventId((e) => e || data.events[0].id);
      }
    } catch { logout(); }
  };
  useEffect(() => { if (token) loadCtx(); }, [token]); // eslint-disable-line

  const curEvent = ctx?.events?.find((e) => e.id === eventId);
  useEffect(() => {
    if (curEvent?.checkpoints?.[0]) setCheckpointId(curEvent.checkpoints[0].id);
  }, [eventId, ctx]); // eslint-disable-line

  const loadStats = async () => {
    if (!eventId) return;
    try { const { data } = await capi.get(`/checkin/stats?event_id=${eventId}${checkpointId ? `&checkpoint_id=${checkpointId}` : ""}`); setStats(data); }
    catch {}
  };
  useEffect(() => { loadStats(); }, [eventId, checkpointId]); // eslint-disable-line

  const login = async () => {
    setLoading(true);
    try {
      const body = tab === "staff"
        ? { mode: "staff", email: form.email, pin: form.pin }
        : { mode: "event", event_code: form.event_code, pin: form.pin };
      const { data } = await axios.post(`${API}/checkin/login`, body);
      sessionStorage.setItem(CK_KEY, data.token);
      setToken(data.token);
      toast.success(`Signed in as ${data.checker.name}`);
    } catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
  };

  const logout = () => { sessionStorage.removeItem(CK_KEY); setToken(""); setCtx(null); setResult(null); };

  const doScan = async (raw) => {
    if (!raw || !eventId || !checkpointId || scanning) return;
    setScanning(true);
    try {
      const { data } = await capi.post("/checkin/scan", { code: raw.trim(), event_id: eventId, checkpoint_id: checkpointId });
      setResult({ ok: true, ...data });
      if (data.status === "already_checked_in") toast.info(data.message);
      else toast.success(data.message);
      setCode("");
      loadStats();
    } catch (e) {
      setResult({ ok: false, message: apiErr(e) });
      toast.error(apiErr(e));
    } finally { setTimeout(() => setScanning(false), 600); }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-foreground grain grid place-items-center p-4">
        <div className="w-full max-w-sm rounded-2xl bg-card p-6 shadow-2xl">
          <Logo />
          <h1 className="font-heading text-2xl font-semibold tracking-tight mt-5">Ticket Checker</h1>
          <p className="text-sm text-muted-foreground">Sign in to scan tickets at the gate.</p>
          <div className="mt-5 grid grid-cols-2 gap-2 p-1 rounded-full bg-muted">
            {[["staff", "Staff login"], ["event", "Event PIN"]].map(([v, l]) => (
              <button key={v} onClick={() => setTab(v)} data-testid={`checker-tab-${v}`}
                className={`h-9 rounded-full text-sm font-semibold transition-colors ${tab === v ? "bg-foreground text-background" : "text-muted-foreground"}`}>{l}</button>
            ))}
          </div>
          <div className="mt-5 space-y-3">
            {tab === "staff" ? (
              <div><Label>Email</Label><Input data-testid="checker-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="checker@eventra.example" className="h-11 mt-1.5" /></div>
            ) : (
              <div><Label>Event code</Label><Input data-testid="checker-event-code" value={form.event_code} onChange={(e) => setForm({ ...form, event_code: e.target.value.toUpperCase() })} placeholder="e.g. IX0XH5" className="h-11 mt-1.5 font-mono tracking-widest" /></div>
            )}
            <div><Label>PIN</Label><Input data-testid="checker-pin" type="password" value={form.pin} onChange={(e) => setForm({ ...form, pin: e.target.value })} placeholder="••••" className="h-11 mt-1.5 font-mono tracking-widest" /></div>
            <Button onClick={login} disabled={loading} className="w-full h-11 rounded-full" data-testid="checker-login-btn">{loading ? "Signing in…" : "Sign in"}</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 glass border-b border-border">
        <div className="mx-auto max-w-2xl px-4 h-16 flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground hidden sm:inline">{ctx?.name}</span>
            <Button variant="outline" size="sm" onClick={logout} className="rounded-full" data-testid="checker-logout"><SignOut size={15} className="mr-1" /> Exit</Button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-2xl px-4 py-6">
        <div className="grid sm:grid-cols-2 gap-3 mb-5">
          <div>
            <Label className="mb-1.5 block">Event</Label>
            <Select value={eventId} onValueChange={setEventId}>
              <SelectTrigger data-testid="checker-event-select"><SelectValue placeholder="Select event" /></SelectTrigger>
              <SelectContent>{ctx?.events?.map((e) => <SelectItem key={e.id} value={e.id}>{e.title}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="mb-1.5 block">Checkpoint</Label>
            <Select value={checkpointId} onValueChange={setCheckpointId}>
              <SelectTrigger data-testid="checker-checkpoint-select"><SelectValue placeholder="Select checkpoint" /></SelectTrigger>
              <SelectContent>{(curEvent?.checkpoints || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>

        {stats && (
          <div className="grid grid-cols-3 gap-3 mb-5">
            <Stat label="Sold" value={stats.tickets_sold} />
            <Stat label="Checked in" value={stats.checked_in} accent />
            <Stat label="At gate" value={stats.checkpoint_scanned ?? "—"} />
          </div>
        )}

        <div className="rounded-xl border border-border bg-card p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2"><QrCode size={22} className="text-primary" /><h2 className="font-heading text-lg font-semibold">Scan ticket</h2></div>
            <Button variant="outline" size="sm" onClick={() => setUseCamera((c) => !c)} className="rounded-full" data-testid="checker-toggle-camera">
              {useCamera ? <><Keyboard size={15} className="mr-1" /> Manual</> : <><Camera size={15} className="mr-1" /> Camera</>}
            </Button>
          </div>

          {useCamera ? (
            <QrScanner active={useCamera && !!eventId && !!checkpointId} onDetected={doScan} />
          ) : (
            <div className="flex gap-2">
              <Input value={code} onChange={(e) => setCode(e.target.value)} onKeyDown={(e) => e.key === "Enter" && doScan(code)} placeholder="Paste QR content or code" className="font-mono" data-testid="checker-manual-code" />
              <Button onClick={() => doScan(code)} className="rounded-full px-6" data-testid="checker-scan-btn">Validate</Button>
            </div>
          )}
          <p className="text-xs text-muted-foreground mt-2">Point the camera at the attendee's QR, or switch to manual entry. Repeat scans safely show the original check-in status.</p>

          {result && (
            <div className={`mt-4 rounded-lg p-4 flex items-start gap-3 ${result.ok ? (result.status === "already_checked_in" ? "bg-sky-500/10 border border-sky-500/30" : "bg-emerald-500/10 border border-emerald-500/30") : "bg-red-500/10 border border-red-500/30"}`} data-testid="checker-result">
              {result.ok ? (result.status === "already_checked_in" ? <Info weight="fill" className="text-sky-400 mt-0.5 shrink-0" size={24} /> : <CheckCircle weight="fill" className="text-emerald-400 mt-0.5 shrink-0" size={24} />) : <XCircle weight="fill" className="text-red-400 mt-0.5 shrink-0" size={24} />}
              <div>
                <div className="font-semibold">{result.message}</div>
                {result.ticket && (
                  <div className="text-sm text-muted-foreground mt-0.5">
                    {result.ticket.category} · {result.ticket.holder || "Guest"}
                    {result.ticket.access?.length ? <span className="inline-flex flex-wrap gap-1 ml-1">{result.ticket.access.map((a, i) => <span key={i} className="px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 text-[11px] font-semibold">{a}</span>)}</span> : null}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
